/**
 * 네이티브(Capacitor) refresh 토큰 보관소 — **Keychain(iOS) / Keystore(Android)**.
 *
 * 웹은 refresh 를 HttpOnly 쿠키로 보관하므로 이 모듈은 **네이티브 전용**이다(웹에선 no-op/null).
 * 네이티브는 쿠키 지속성이 불안정(WebView/CapacitorHttp)하므로 refresh 를 기기 보안저장소에 두고
 * `X-Refresh-Token` 헤더로 전송한다.
 *
 * 백엔드: `@aparajita/capacitor-secure-storage` — iOS Keychain / Android EncryptedSharedPreferences(Keystore).
 *
 * 모든 호출은 try/catch 가드 — 미지원 셸(구버전 네이티브, plugin 미포함)에서 throw 해도
 * 로그인 화면이 크래시하지 않도록 한다(§18(f)). 플러그인은 네이티브 바이너리에 포함되므로
 * (OTA 불가) 신 바이너리를 배포해야 동작한다.
 */
import { isNative, nativeWarn } from "../native";

const STORAGE_KEY = "refreshToken";

/** 네이티브 보안저장소에서 refresh 토큰 읽기. 웹/실패 시 null. */
export async function readNativeRefresh(): Promise<string | null> {
  if (!isNative()) return null;
  try {
    const { SecureStorage } = await import("@aparajita/capacitor-secure-storage");
    return await SecureStorage.getItem(STORAGE_KEY);
  } catch (err) {
    nativeWarn("refreshStore.read", err);
    return null;
  }
}

/** 네이티브 보안저장소에 refresh 토큰 쓰기/삭제(null). 웹은 no-op. */
export async function writeNativeRefresh(token: string | null): Promise<void> {
  if (!isNative()) return;
  try {
    const { SecureStorage } = await import("@aparajita/capacitor-secure-storage");
    if (token) {
      await SecureStorage.setItem(STORAGE_KEY, token);
    } else {
      await SecureStorage.removeItem(STORAGE_KEY);
    }
  } catch (err) {
    nativeWarn("refreshStore.write", err);
  }
}
